import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../Layout/Layout';
import { useCustomerProfile } from '../../queries/customers';
import './customer-profile.css';

/**
 * Customer 360 — one guest, every system in one place. Reads the aggregate from
 * GET /customers/{id}/profile (loyalty points/orders/spend/favorite, spins,
 * birthdays, email engagement) and renders a hero, auto-computed insight chips,
 * stat tiles, next-reward progress, and a unified activity timeline. Honey & Ink
 * styled, bilingual EN/AR. Insight + timeline copy is localized here from the
 * backend's {key, params}. See the Customer 360 proposal.
 */
export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.split('-')[0] || 'en';
  const isAr = lng === 'ar';

  const { data, isLoading, isError } = useCustomerProfile(id);

  return (
    <Layout>
      <div className="c360">
        <button type="button" className="c-back" onClick={() => navigate('/customers')}>
          <i className="pi pi-arrow-left" /> <span>{t('customers.profile.back')}</span>
        </button>

        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <div className="empty">{t('customers.profile.not_found')}</div>
        ) : (
          <Profile data={data} t={t} lng={lng} isAr={isAr} />
        )}
      </div>
    </Layout>
  );
}

function Profile({ data, t, lng, isAr }) {
  const navigate = useNavigate();
  const c = data.customer || {};
  const s = data.stats || {};
  const cur = data.currency || '';
  const name = c.name || c.phone || t('customers.this_customer');

  return (
    <>
      {/* hero */}
      <div className="hero">
        <div className="avatar">{initials(c.name, c.phone)}</div>
        <div className="hero-main">
          <div className="hero-name">
            {name}
            {data.tier && <span className={`tier ${data.tier}`}>{t(`customers.profile.tier.${data.tier}`)}</span>}
          </div>
          <div className="hero-sub">
            {c.phone && <span className="mono" dir="ltr">{c.phone}</span>}
            {c.phone && (c.email || c.source) && <span className="sep" />}
            {c.email && <span dir="ltr">{c.email}</span>}
            {c.email && c.source && <span className="sep" />}
            {c.source && <span>{t(`customers.source.${c.source}`)}</span>}
            <span className="sep" />
            <span>{c.last_visit
              ? t('customers.profile.last_seen', { when: timeAgo(c.last_visit, lng) })
              : t('customers.profile.last_seen_never')}</span>
          </div>
        </div>
      </div>

      {/* auto-insight chips — clickable ones open the matching customer segment */}
      {data.insights?.length > 0 && (
        <div className="chips">
          {data.insights.map((ins, i) => (
            SEGMENT_BADGES.has(ins.key) ? (
              <button type="button" className={`chip chip-link ${ins.tone}`} key={`${ins.key}-${i}`}
                onClick={() => navigate(`/customers?segment=${ins.key}`)}
                title={t('customers.profile.see_segment')}>
                {insightText(t, ins, isAr)} <i className="pi pi-angle-right" style={{ fontSize: '.7rem' }} />
              </button>
            ) : (
              <span className={`chip ${ins.tone}`} key={`${ins.key}-${i}`}>{insightText(t, ins, isAr)}</span>
            )
          ))}
        </div>
      )}

      {/* stat tiles */}
      <div className="tiles">
        <Tile ic="🚪" label={t('customers.profile.stat_visits')} value={s.visits ?? 0} />
        <Tile ic="💳" label={t('customers.profile.stat_spent')}
          value={<>{fmt(s.total_spent ?? 0, lng)}{cur && <small>{cur}</small>}</>}
          sub={spendTrend(t, s)} />
        {data.loyalty_enabled && (
          <Tile ic="🎁" label={t('customers.profile.stat_points')} value={s.points_balance ?? 0}
            sub={data.next_reward
              ? { cls: 'muted', text: t('customers.profile.points_of', { cost: data.next_reward.cost }) }
              : null} />
        )}
        <Tile ic="☕" label={t('customers.profile.stat_favorite')} sm
          value={s.favorite_item
            ? <>{isAr && s.favorite_item.name_ar ? s.favorite_item.name_ar : s.favorite_item.name}<small>×{s.favorite_item.count}</small></>
            : '—'} />
        <Tile ic="🎡" label={t('customers.profile.stat_spins')} value={s.spins_won ?? 0}
          sub={s.spins_unredeemed > 0 ? { cls: 'muted', text: t('customers.profile.spins_unredeemed', { n: s.spins_unredeemed }) } : null} />
        <Tile ic="✉️" label={t('customers.profile.stat_emails')}
          value={<>{s.emails_sent ?? 0}<small>/ {s.emails_opened ?? 0} {t('customers.profile.opened')}</small></>} />
      </div>

      {/* next reward */}
      {data.loyalty_enabled && data.next_reward && (
        <div className="reward">
          <div className="rh">
            <div className="rt">{t('customers.profile.next_reward')} · <b>{isAr && data.next_reward.title_ar ? data.next_reward.title_ar : data.next_reward.title}</b></div>
            <div className="rmeta">{t('customers.profile.reward_remaining', { n: data.next_reward.remaining })}</div>
          </div>
          <div className="bar"><span style={{ width: `${rewardPct(s.points_balance, data.next_reward.cost)}%` }} /></div>
        </div>
      )}

      {/* timeline */}
      <div className="tl-card">
        <div className="tl-head"><span>🕒</span><span className="h">{t('customers.profile.timeline')}</span></div>
        {data.timeline?.length > 0 ? (
          <ul className="tl">
            {data.timeline.map((ev, i) => (
              <li key={i}>
                <span className={`ic ${ev.type}`}>{EV_ICON[ev.type] || '•'}</span>
                <div className="body">
                  <div className="t" dangerouslySetInnerHTML={{ __html: eventText(t, ev, isAr, lng, cur) }} />
                  {eventMeta(t, ev) && <div className="m">{eventMeta(t, ev)}</div>}
                </div>
                <span className="when">{timeAgo(ev.at, lng)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="empty">{t('customers.profile.no_activity')}</div>
        )}
      </div>
    </>
  );
}

function Tile({ ic, label, value, sub, sm }) {
  return (
    <div className="tile">
      <div className="k"><span className="ic">{ic}</span>{label}</div>
      <div className={`v${sm ? ' sm' : ''}`}>{value}</div>
      {sub && <div className={`sub ${sub.cls}`}>{sub.text}</div>}
    </div>
  );
}

function Loading() {
  return (
    <>
      <Skeleton height="112px" borderRadius="20px" />
      <div className="tiles">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="98px" borderRadius="14px" />)}</div>
      <Skeleton height="260px" borderRadius="20px" className="mt-3" />
    </>
  );
}

const EV_ICON = { checkin: '🚪', order: '🧾', redemption: '🎁', spin: '🎡', birthday: '🎂', email: '✉️' };

// Insight badges that map to a bulk customer segment (mirror CustomerSegmentService).
const SEGMENT_BADGES = new Set(['vip', 'at_risk', 'regular', 'new', 'unreachable', 'birthday_soon', 'lucky']);

/* ── helpers ───────────────────────────────────────────────────────────── */

function initials(name, phone) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]).join('').toUpperCase();
  }
  if (phone) return phone.slice(-2);
  return '👤';
}

function fmt(n, lng) {
  try { return new Intl.NumberFormat(lng === 'ar' ? 'ar-EG' : 'en-US').format(n); }
  catch { return String(n); }
}

function rewardPct(balance, cost) {
  if (!cost) return 0;
  return Math.max(0, Math.min(100, Math.round(((balance ?? 0) / cost) * 100)));
}

function spendTrend(t, s) {
  const cur = s.spend_this_month ?? 0;
  const prev = s.spend_last_month ?? 0;
  if (!prev && !cur) return null;
  if (!prev) return { cls: 'up', text: t('customers.profile.trend_new') };
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return { cls: 'muted', text: t('customers.profile.trend_flat') };
  return { cls: pct > 0 ? 'up' : 'down', text: t('customers.profile.trend_vs', { pct: `${pct > 0 ? '+' : ''}${pct}` }) };
}

function insightText(t, ins, isAr) {
  const p = { ...(ins.params || {}) };
  if (ins.key === 'loves_item') p.name = isAr && p.name_ar ? p.name_ar : p.name;
  if (ins.key === 'near_reward') p.title = isAr && p.title_ar ? p.title_ar : p.title;
  return t(`customers.profile.insight.${ins.key}`, p);
}

const esc = (v) => String(v ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));

function eventText(t, ev, isAr, lng, cur) {
  switch (ev.type) {
    case 'checkin':
      return t('customers.profile.ev_checkin', { points: ev.points ?? 0 });
    case 'redemption':
      return t('customers.profile.ev_redemption', { points: ev.points ?? 0 });
    case 'order': {
      const item = isAr && ev.item_name_ar ? ev.item_name_ar : ev.item_name;
      return item
        ? t('customers.profile.ev_order', { item: `<b>${esc(item)}</b>` })
        : t('customers.profile.ev_order_generic');
    }
    case 'spin':
      return ev.prize
        ? t('customers.profile.ev_spin', { prize: `<b>${esc(ev.prize)}</b>` })
        : t('customers.profile.ev_spin_generic');
    case 'birthday':
      return t('customers.profile.ev_birthday');
    case 'email':
      return t(ev.opened ? 'customers.profile.ev_email_opened' : 'customers.profile.ev_email',
        { subject: esc(ev.subject || t('customers.profile.an_email')) });
    default:
      return '';
  }
}

function eventMeta(t, ev) {
  if (ev.type === 'order') {
    const bits = [];
    if (ev.quantity > 1) bits.push(`×${ev.quantity}`);
    if (ev.total) bits.push(`${ev.total}`);
    bits.push(ev.paid ? t('customers.profile.paid') : t('customers.profile.unpaid'));
    return bits.join(' · ');
  }
  if (ev.type === 'spin' && ev.redeemed === false) return t('customers.profile.unredeemed');
  return null;
}

function timeAgo(iso, lng) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(lng === 'ar' ? 'ar' : 'en', { numeric: 'auto' });
    if (abs < 60) return rtf.format(Math.round(diff), 'second');
    if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
    if (abs < 604800) return rtf.format(Math.round(diff / 86400), 'day');
    if (abs < 2629800) return rtf.format(Math.round(diff / 604800), 'week');
    return rtf.format(Math.round(diff / 2629800), 'month');
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}
